/**
 * Product cubes are served by the host site, the same origin this bundle runs
 * on. Bundling copies through Vite would freeze the apps origin into the URL at
 * build time and break the local `.loc` stack.
 */
const CUBE_DIR = "/images/pages/products/image";

const cube = (name: string): string => `${CUBE_DIR}/${name}.webp`;

/**
 * The catalogue names a cube by the prototype's file ("air-tracking.png"); the
 * host site keeps the same artwork under the same base name as webp. Only the
 * base name is kept, so a stray path in the catalogue cannot point elsewhere.
 */
export const cubeUrl = (file: string): string => {
  const base = file.split("/").pop() ?? "";

  return cube(base.replace(/\.[a-z0-9]+$/i, ""));
};

/**
 * `service_tool` values the back office knows about. Only these can carry a
 * subscription or an API key; the rest of the catalogue is sold on request.
 */
export type ServiceTool =
  | "container-tracking"
  | "air-tracking"
  | "vessel-schedules"
  | "load-calculator"
  | "transit-time"
  | "rate-engine"
  | "co2";

export type Product = {
  /** Stable id used for React keys and quote links; equals `serviceTool` where there is one. */
  id: string;
  /** Set when the back office can meter this product. */
  serviceTool: ServiceTool | null;
  name: string;
  /** One line, as shown on the "Not active" cards. */
  blurb: string;
  cube: string;
  /**
   * Where "Learn more" goes. Products with their own `/api/*` page link to it;
   * the three without one follow the site header, which sends them to the
   * catalogue or the live tool.
   */
  href: string;
};

/**
 * The nine products the marketing site sells, in the order the site's own
 * Products menu lists them. Names here win over the back office enum labels:
 * they are what the customer sees everywhere else.
 */
export const PRODUCTS: Product[] = [
  {
    id: "container-tracking",
    serviceTool: "container-tracking",
    name: "Container Tracking",
    blurb: "Live container status across 180+ carriers.",
    cube: cube("container-tracking"),
    href: "/api/container-tracking",
  },
  {
    id: "air-tracking",
    serviceTool: "air-tracking",
    name: "Air Tracking",
    blurb: "Air waybill milestones, flight by flight.",
    cube: cube("air-tracking"),
    href: "/api/air-tracking",
  },
  {
    id: "global-tracking",
    serviceTool: null,
    name: "Global Tracking",
    blurb: "Sea, air, rail and road in one feed.",
    cube: cube("global-tracking"),
    href: "/tracking",
  },
  {
    id: "vessel-schedules",
    serviceTool: "vessel-schedules",
    name: "Vessel Schedules",
    blurb: "Sailing schedules by route or vessel.",
    cube: cube("vessel-schedules"),
    href: "/api/vessel-schedules",
  },
  {
    id: "transit-time",
    serviceTool: "transit-time",
    name: "Transit Time",
    blurb: "Port-to-port benchmarks and outliers.",
    cube: cube("transit-time"),
    href: "/api/transit-time",
  },
  {
    id: "rate-engine",
    serviceTool: "rate-engine",
    name: "Freight Booking Platform",
    blurb: "Freight rates and quotes on demand.",
    cube: cube("rate-engine"),
    href: "/products",
  },
  {
    id: "co2",
    serviceTool: "co2",
    name: "CO₂ Intelligence",
    blurb: "Emissions per shipment, audit-ready.",
    cube: cube("co2-intelligence"),
    href: "/api/carbon-emissions",
  },
  {
    id: "load-calculator",
    serviceTool: "load-calculator",
    name: "Cargo Load Optimizer",
    blurb: "Optimal stuffing for containers and pallets.",
    cube: cube("cargo-load-optimizer"),
    href: "/api/load-calculator",
  },
  {
    id: "freight-intelligence",
    serviceTool: null,
    name: "Freight Intelligence",
    blurb: "Market trends and historical indices.",
    cube: cube("freight-intelligence"),
    href: "/products",
  },
];

const PRODUCT_BY_TOOL = new Map<string, Product>(
  PRODUCTS.filter((product) => product.serviceTool !== null).map((product) => [product.serviceTool as string, product])
);

export const findProductByTool = (serviceTool: string | null): Product | undefined =>
  serviceTool === null ? undefined : PRODUCT_BY_TOOL.get(serviceTool);

/** Products a key can be issued against — the metered ones, catalogue order. */
export const METERED_PRODUCTS: Product[] = PRODUCTS.filter((product) => product.serviceTool !== null);

/** Empty-state cube, also served by the host site. */
export const EMPTY_STATE_CUBE = "/images/pages/cabinet/green.webp";
